import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { resetPassword } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, KeyRound, CheckCircle2 } from "lucide-react";

const inputCls =
  "rounded-none border-[#0A1F44]/30 bg-transparent focus-visible:ring-1 focus-visible:ring-[#C0982E] focus-visible:border-[#C0982E] font-mono";

const isValid = (pw) => pw.length >= 8 && /\d/.test(pw) && /[a-zA-Z]/.test(pw);

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!isValid(next)) {
      toast.error("Password must be at least 8 characters and include a number and a letter");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, next);
      setDone(true);
      toast.success("Password reset successfully");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-[#F3F5F8] flex flex-col items-center justify-center px-5">
      <Link to="/" className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-[#0A1F44] text-[#F3F5F8] flex items-center justify-center">
          <GraduationCap className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="leading-none">
          <div className="font-serif text-xl text-[#0A1F44]">North East Fine Arts</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/50">Staff Directory</div>
        </div>
      </Link>

      <div className="w-full max-w-sm border border-[#0A1F44] bg-white p-7">
        {!token ? (
          <div className="text-center space-y-4">
            <h1 className="font-serif text-2xl text-[#0A1F44]">Invalid link</h1>
            <p className="text-[#0A1F44]/60 text-sm">This reset link is missing its token. Please request a new one from the sign-in screen.</p>
            <Button data-testid="reset-home-btn" onClick={() => navigate("/")} className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E]">Back to Directory</Button>
          </div>
        ) : done ? (
          <div className="text-center space-y-4">
            <CheckCircle2 className="w-10 h-10 text-[#C0982E] mx-auto" strokeWidth={1.5} />
            <h1 className="font-serif text-2xl text-[#0A1F44]">Password reset</h1>
            <p className="text-[#0A1F44]/60 text-sm">Your admin password has been updated. Sign in with your new password.</p>
            <Button data-testid="reset-done-home-btn" onClick={() => navigate("/")} className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E]">Go to Directory</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <h1 className="font-serif text-2xl text-[#0A1F44] flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#C0982E]" strokeWidth={1.5} /> Reset Password
            </h1>
            <p className="text-[#0A1F44]/60 text-sm">Choose a new password (at least 8 characters, including a number and a letter).</p>
            <div>
              <Label className="text-xs uppercase tracking-wider">New Password</Label>
              <Input data-testid="reset-new" type="password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} autoFocus />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Confirm New Password</Label>
              <Input data-testid="reset-confirm" type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
            <Button type="submit" data-testid="reset-submit" disabled={loading} className="w-full rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E]">
              {loading ? "Resetting…" : "Reset Password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
